import { exp } from "./export";
import { HiddenCanvas, utils } from '../utils.js';
import { isIOS } from '../core/environment.js';
import { star } from '../patterns/star.js';
import { settings } from "../settings";
import { lang } from "../lang";
import { beads } from "../beads";

exp.getSplitImages = (recentViewSettings, projectWidth, projectHeight) => { // for large RAW and huichol projects

    // calculate how many images we need

    let neededRows = Math.ceil(projectHeight / 1000);
    let neededColumns = Math.ceil(projectWidth / 1000);

    let getPic = (i, j) => {
        let hidden = new HiddenCanvas(1000, 1000);

        beads.rt.forEach(element => {

            let topLeft = element.canvas.getTopLeft();
            let x = i * 1000, y = j * 1000;
            let w = element.canvas.canvas.width;
            let h = element.canvas.canvas.height;
            let place = {
                x: topLeft.x,
                y: topLeft.y
            };

            if (isIOS || window.safari) {
                [x, y, w, h] = utils.safariDraw(x, y, w, h, element.canvas);
            }
            if (w > 0 && h > 0) {
                hidden.ctx.drawImage(
                    element.canvas.canvas,
                    x, // start clipping
                    y, // start clipping
                    w, // clipping width
                    h, // clipping height
                    place.x, // place x
                    place.y, // place y
                    w, // place width
                    h // place height
                );
            }
        });

        let myData = hidden.canvas.toDataURL();
        hidden.remove();

        return {
            image: myData,
            fit: [500, 500],
            style: 'paragraphText',
        };
    };

    let result = [];

    for (let i = 0; i < neededColumns; i++) {
        for (let j = 0; j < neededRows; j++) {
            result.push(getPic(i, j));
        }
    }

    // restore view

    settings.setTheme(recentViewSettings.theme);
    settings.showLetterCodes = recentViewSettings.showLetterCodes;
    settings.renderColorOnly = recentViewSettings.simpleMode;
    beads.refreshCanvas();

    return result;
};

exp.getImage = () => { // create condensed image of entire project

    let recentViewSettings = {
        theme: settings.theme.displayName,
        showLetterCodes: settings.showLetterCodes,
        simpleMode: settings.renderColorOnly
    };

    settings.showLetterCodes = false;
    if (settings.pattern.alwaysShowLetterCodesOnExport) settings.showLetterCodes = true;

    settings.showInactiveBeads = false;
    settings.renderColorOnly = false;

    if (settings.pattern.simpleExport) {
        settings.showLetterCodes = true;
        settings.renderColorOnly = true;
    }

    settings.setTheme(lang.get('light theme option', 'Light'));

    let projectWidth = settings.columns * settings.pattern.colDist();
    let projectHeight = settings.rows * settings.pattern.rowDist();

    projectWidth += 50;

    if (settings.pattern.getExportImageDimensions) {
        let myDimensions = settings.pattern.getExportImageDimensions(settings.rows);
        projectWidth = myDimensions.width;
        projectHeight = myDimensions.height;
    }

    let longerSide = Math.max(projectWidth, projectHeight);
    let scale = (longerSide > 1000) ? 1000 / longerSide : 1;

    if (settings.pattern.splitExportImage) { // (RAW and huichol)
        if (longerSide > 1500) {
            return exp.getSplitImages(recentViewSettings, projectWidth, projectHeight);
        }
    }

    let hidden = new HiddenCanvas(projectWidth * scale, projectHeight * scale);

    beads.rt.forEach(element => {

        let topLeft = element.canvas.getTopLeft();
        let x = 0, y = 0;
        let w = element.canvas.canvas.width;
        let h = element.canvas.canvas.height;
        let place = {
            x: topLeft.x,
            y: topLeft.y
        };

        if (!settings.pattern.useSingleRenderTexture && !settings.pattern.useFourRenderTextures) {
            if (topLeft.x === 0) {
                x = 60;
                w -= 60;
            } else {
                place.x -= 60;
            }
            if (topLeft.y === 0) {
                y = 85;
                h -= 85;
            } else {
                place.y -= 85;
            }
        }

        if (settings.pattern.name === 'medallion') {
            [x, y] = settings.pattern.getRTLocation()
        }

        if (isIOS || window.safari) {
            [x, y, w, h] = utils.safariDraw(x, y, w, h, element.canvas);
        }

        if (w > 0 && h > 0) {
            hidden.ctx.drawImage(
                element.canvas.canvas,
                x, // start clipping
                y, // start clipping
                w, // clipping width
                h, // clipping height
                place.x * scale, // place x
                place.y * scale, // place y
                w * scale, // place width
                h * scale // place height
            );
        }
    });

    let result = hidden.canvas.toDataURL();

    if (settings.pattern.rotateCamera) { // for peyote... rotate
        let anotherCanvas = new HiddenCanvas(hidden.canvas.height, hidden.canvas.width);

        anotherCanvas.ctx.translate(anotherCanvas.canvas.width/2, anotherCanvas.canvas.height/2);
        anotherCanvas.ctx.rotate(0.5 * Math.PI);
        anotherCanvas.ctx.drawImage(hidden.canvas, -hidden.canvas.width/2, -hidden.canvas.height/2, hidden.canvas.width, hidden.canvas.height);
        anotherCanvas.ctx.rotate(-0.5 * Math.PI);
        anotherCanvas.ctx.translate(-anotherCanvas.canvas.width/2, -anotherCanvas.canvas.height/2);
        result = anotherCanvas.canvas.toDataURL();
        anotherCanvas.remove();
    }

    hidden.remove();

    // restore view

    settings.setTheme(recentViewSettings.theme);
    settings.showLetterCodes = recentViewSettings.showLetterCodes;
    settings.renderColorOnly = recentViewSettings.simpleMode;
    beads.refreshCanvas();

    return {
        image: result,
        fit: [500, 500],
        style: 'paragraphText',
    };
};

exp.getProjectImage = () => {
    if (settings.pattern.name !== 'star') {
        return exp.getImage();
    }
    const result = [];
    result.push({text: 'Front View', style: 'subheader'});
    star.changeView('F');
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Back View', style: 'subheader'});
    star.changeView('B');
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Warped square 1', style: 'subheader'});
    star.changeView(1);
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Warped square 2', style: 'subheader'});
    star.changeView(2);
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Warped square 3', style: 'subheader'});
    star.changeView(3);
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Warped square 4', style: 'subheader'});
    star.changeView(4);
    result.push(exp.getImage());
    result.push({text: ' ', style: 'myBreak'});
    result.push({text: 'Warped square 5', style: 'subheader'});
    star.changeView(5);
    result.push(exp.getImage());
    return result;
};
